import type {
  FillGradient,
  FillMode,
  ObjectGlow,
  ObjectShadow,
  SceneObject,
  ShapeType,
  StrokeSides,
} from '../types';
import {
  parseFillGradient,
  parseFillMode,
  parseObjectGlow,
  parseObjectShadow,
} from './objectVisualStyle';
import { parseStrokeSides } from './strokeSides';

export interface ShapeBodyData {
  shapeType?: ShapeType;
  pathData?: string;
  fill?: string;
  fillMode?: FillMode;
  fillGradient?: FillGradient;
  stroke?: string;
  strokeWidth?: number;
  strokeStyle?: SceneObject['strokeStyle'];
  strokeSides?: StrokeSides;
  shadow?: ObjectShadow;
  glow?: ObjectGlow;
}

export function parseShapeBody(body: string | undefined | null): ShapeBodyData {
  if (!body) return {};
  try {
    const parsed = (JSON.parse(body) as ShapeBodyData) ?? {};
    if (parsed.strokeSides) {
      parsed.strokeSides = parseStrokeSides(parsed.strokeSides);
    }
    parsed.fillMode = parseFillMode(parsed.fillMode) ?? parsed.fillMode;
    parsed.fillGradient = parseFillGradient(parsed.fillGradient) ?? parsed.fillGradient;
    parsed.shadow = parseObjectShadow(parsed.shadow) ?? parsed.shadow;
    parsed.glow = parseObjectGlow(parsed.glow) ?? parsed.glow;
    return parsed;
  } catch {
    return {};
  }
}

const SHAPE_STYLE_KEYS = [
  'shapeType',
  'pathData',
  'fill',
  'fillMode',
  'fillGradient',
  'stroke',
  'strokeWidth',
  'strokeStyle',
  'strokeSides',
  'shadow',
  'glow',
] as const;

export function shapeBodyFromSceneObject(
  obj: Pick<SceneObject, (typeof SHAPE_STYLE_KEYS)[number]>
): ShapeBodyData {
  const data: ShapeBodyData = {
    shapeType: obj.shapeType ?? 'rectangle',
  };
  if (obj.pathData) data.pathData = obj.pathData;
  if (obj.fill != null) data.fill = obj.fill;
  if (obj.fillMode != null) data.fillMode = obj.fillMode;
  if (obj.fillGradient != null) data.fillGradient = obj.fillGradient;
  if (obj.stroke != null) data.stroke = obj.stroke;
  if (obj.strokeWidth != null) data.strokeWidth = obj.strokeWidth;
  if (obj.strokeStyle != null) data.strokeStyle = obj.strokeStyle;
  if (obj.strokeSides != null) data.strokeSides = obj.strokeSides;
  if (obj.shadow != null) data.shadow = obj.shadow;
  if (obj.glow != null) data.glow = obj.glow;
  return data;
}

export function serializeShapeBody(data: ShapeBodyData): string {
  return JSON.stringify(
    shapeBodyFromSceneObject({
      shapeType: data.shapeType,
      pathData: data.pathData,
      fill: data.fill,
      fillMode: data.fillMode,
      fillGradient: data.fillGradient,
      stroke: data.stroke,
      strokeWidth: data.strokeWidth,
      strokeStyle: data.strokeStyle,
      strokeSides: data.strokeSides,
      shadow: data.shadow,
      glow: data.glow,
    } as SceneObject)
  );
}

export function resolveShapeFieldsFromBody(
  type: string,
  body: string | undefined
): Pick<SceneObject, (typeof SHAPE_STYLE_KEYS)[number]> {
  if (type !== 'shape') return {};
  const parsed = parseShapeBody(body);
  const shapeType = parsed.shapeType ?? (parsed.pathData ? 'path' : 'rectangle');
  return {
    shapeType,
    pathData: parsed.pathData,
    fill: parsed.fill,
    fillMode: parsed.fillMode,
    fillGradient: parsed.fillGradient,
    stroke: parsed.stroke,
    strokeWidth: parsed.strokeWidth,
    strokeStyle: parsed.strokeStyle,
    strokeSides: parsed.strokeSides,
    shadow: parsed.shadow,
    glow: parsed.glow,
  };
}
